import { Button, Form, InputNumber, Select, Table, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useMemo } from "react";

const { Text } = Typography;

/**
 * Dynamic bill line items: expenditure from Import C&F / Export C&F settings + amount.
 * Selecting a particular auto-fills amount when the setting defines one.
 */
export default function ExpenditureParticularsTable({
  expenditureOptions,
  disabled,
  loadingOptions,
  showTotalSummary = false,
}) {
  const form = Form.useFormInstance();
  const billDetails = Form.useWatch("bill_details", form);

  const totalExpenses = useMemo(
    () =>
      (billDetails || []).reduce(
        (sum, row) => sum + (Number(row?.amount) || 0),
        0,
      ),
    [billDetails],
  );

  const amountByExpenditureId = useMemo(() => {
    const map = new Map();
    (expenditureOptions || []).forEach((option) => {
      if (
        option?.value != null &&
        option?.amount !== undefined &&
        option?.amount !== null
      ) {
        map.set(option.value, Number(option.amount));
      }
    });
    return map;
  }, [expenditureOptions]);

  const onExpenditureSelect = (fieldName, expenditureId) => {
    const amount = amountByExpenditureId.get(expenditureId);
    if (amount !== undefined) {
      form.setFieldValue(["bill_details", fieldName, "amount"], amount);
    }
  };

  return (
    <Form.List name="bill_details">
      {(fields, { add, remove }) => (
        <>
          <Table
            size="small"
            pagination={false}
            rowKey={(field) => field.key}
            dataSource={fields}
            columns={[
              {
                title: "Particulars",
                width: "55%",
                render: (_, field) => (
                  <Form.Item
                    name={[field.name, "expenditure_id"]}
                    rules={[
                      { required: true, message: "Select expenditure type" },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      showSearch
                      allowClear
                      placeholder="Select from C&F settings"
                      loading={loadingOptions}
                      disabled={disabled}
                      optionFilterProp="label"
                      options={expenditureOptions}
                      popupMatchSelectWidth={false}
                      style={{ minWidth: 260 }}
                      onSelect={(expenditureId) =>
                        onExpenditureSelect(field.name, expenditureId)
                      }
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Amount",
                width: "35%",
                render: (_, field) => (
                  <Form.Item
                    name={[field.name, "amount"]}
                    rules={[{ required: true, message: "Enter amount" }]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      min={0}
                      style={{ width: "100%" }}
                      disabled={disabled}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "",
                width: 72,
                render: (_, field) =>
                  !disabled && fields.length > 1 ? (
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                      aria-label="Remove row"
                    />
                  ) : null,
              },
            ]}
            summary={
              showTotalSummary && fields.length > 0
                ? () => (
                    <Table.Summary>
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0} align="right">
                          <Text strong>Total Expenses (Taka)</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1}>
                          <Text strong>{totalExpenses.toLocaleString()}</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} />
                      </Table.Summary.Row>
                    </Table.Summary>
                  )
                : undefined
            }
          />
          {!disabled && (
            <Button
              type="dashed"
              onClick={() => add()}
              block
              style={{ marginTop: 8 }}
            >
              Add row
            </Button>
          )}
        </>
      )}
    </Form.List>
  );
}
